import { defineStore } from 'pinia';
import axios from 'axios';
import { apiUrl, getRoleApiPath } from '@/config/api';

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const TOKEN_TYPE_KEY = 'tokenType';
const USER_ROLE_KEY = 'userRole';
const USER_DETAILS_KEY = 'userDetails';
const REFRESH_BUFFER_MS = 60 * 1000;
const FALLBACK_REFRESH_MS = 10 * 60 * 1000;

let refreshPromise = null;

export const useAuthStore = defineStore('auth', {
  state: () => ({
    accessToken: null,
    refreshToken: null,
    tokenType: null,
    userRole: null,
    userDetails: {},
    sessionInitialized: false,
    refreshTimerId: null,
    requestState: {
      isLoading: false,
      error: null,
    },
  }),
  getters: {
    isAuthenticated(state) {
      return !!state.accessToken;
    },
  },
  actions: {
    /**
     * Persist the current auth payload to localStorage.
     */
    persistSession() {
      if (this.accessToken) {
        localStorage.setItem(ACCESS_TOKEN_KEY, this.accessToken);
      } else {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
      }

      if (this.refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, this.refreshToken);
      } else {
        localStorage.removeItem(REFRESH_TOKEN_KEY);
      }

      if (this.tokenType) {
        localStorage.setItem(TOKEN_TYPE_KEY, this.tokenType);
      } else {
        localStorage.removeItem(TOKEN_TYPE_KEY);
      }

      if (this.userRole) {
        localStorage.setItem(USER_ROLE_KEY, this.userRole);
      } else {
        localStorage.removeItem(USER_ROLE_KEY);
      }

      if (this.userDetails && Object.keys(this.userDetails).length) {
        localStorage.setItem(USER_DETAILS_KEY, JSON.stringify(this.userDetails));
      } else {
        localStorage.removeItem(USER_DETAILS_KEY);
      }
    },

    /**
     * Hydrate the auth state from localStorage.
     */
    loadTokensFromStorage() {
      this.accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
      this.refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      this.tokenType = localStorage.getItem(TOKEN_TYPE_KEY) || 'Bearer';
      this.userRole = localStorage.getItem(USER_ROLE_KEY);

      try {
        this.userDetails = JSON.parse(localStorage.getItem(USER_DETAILS_KEY) || '{}');
      } catch (error) {
        console.warn('Failed to parse cached user details.', error);
        this.userDetails = {};
      }

      if (this.accessToken) {
        this.scheduleTokenRefresh();
      }
    },

    setSession({ accessToken, refreshToken, tokenType }) {
      this.accessToken = accessToken;
      this.refreshToken = refreshToken;
      this.tokenType = tokenType || 'Bearer';
      this.persistSession();
      this.scheduleTokenRefresh();
    },

    setUserDetails(userDetails) {
      this.userDetails = userDetails || {};
      this.userRole = this.userDetails.Role || null;
      this.persistSession();
    },

    clearRefreshTimer() {
      if (this.refreshTimerId) {
        clearTimeout(this.refreshTimerId);
        this.refreshTimerId = null;
      }
    },

    getTokenExpiry(token = this.accessToken) {
      if (!token) {
        return 0;
      }

      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return (payload.exp || 0) * 1000;
      } catch (error) {
        console.warn('Unable to decode token expiry.', error);
        return 0;
      }
    },

    isTokenExpired(bufferMs = 0) {
      const expiresAt = this.getTokenExpiry();
      if (!expiresAt) {
        return true;
      }

      return Date.now() + bufferMs >= expiresAt;
    },

    scheduleTokenRefresh() {
      this.clearRefreshTimer();

      if (!this.accessToken || !this.refreshToken) {
        return;
      }

      const expiresAt = this.getTokenExpiry();
      const refreshDelay = expiresAt
        ? Math.max(expiresAt - Date.now() - REFRESH_BUFFER_MS, 1000)
        : FALLBACK_REFRESH_MS;

      this.refreshTimerId = setTimeout(async () => {
        try {
          await this.refreshAccessToken();
        } catch (error) {
          console.error('Scheduled refresh failed.', error);
          this.logout({ redirectToLogin: true });
        }
      }, refreshDelay);
    },

    async initializeSession() {
      if (this.sessionInitialized) {
        return this.isAuthenticated;
      }

      this.loadTokensFromStorage();

      if (this.refreshToken && (!this.accessToken || this.isTokenExpired(REFRESH_BUFFER_MS))) {
        try {
          await this.refreshAccessToken();
        } catch (error) {
          console.error('Session bootstrap refresh failed.', error);
          this.logout();
        }
      }

      if (this.accessToken && !this.userRole) {
        try {
          await this.fetchUserDetails({ force: true });
        } catch (error) {
          console.error('Session bootstrap user fetch failed.', error);
          this.logout();
        }
      }

      this.sessionInitialized = true;
      return this.isAuthenticated;
    },

    async ensureValidSession() {
      if (!this.accessToken && !this.refreshToken) {
        return false;
      }

      if (this.accessToken && !this.isTokenExpired(REFRESH_BUFFER_MS)) {
        return true;
      }

      try {
        await this.refreshAccessToken();
        return !!this.accessToken;
      } catch (error) {
        console.error('Failed to ensure a valid session.', error);
        return false;
      }
    },

    async login(username, password) {
      this.requestState.isLoading = true;
      this.requestState.error = null;

      try {
        const formData = new FormData();
        formData.append('username', username);
        formData.append('password', password);

        const response = await axios.post(apiUrl('/nora_user/token'), formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        const { access_token, refresh_token, token_type } = response.data;
        this.setSession({
          accessToken: access_token,
          refreshToken: refresh_token,
          tokenType: token_type,
        });

        const userDetails = await this.fetchUserDetails({ force: true });
        if (!['admin', 'clinic_admin', 'state_health_org'].includes(userDetails?.Role)) {
          this.logout();
          throw new Error('Unauthorized role');
        }

        this.sessionInitialized = true;
        return true;
      } catch (error) {
        console.error('Login error:', error);
        const errorMessage = this.handleError(error);
        this.requestState.error = errorMessage;
        return errorMessage;
      } finally {
        this.requestState.isLoading = false;
      }
    },

    async fetchUserRoles() {
      const userDetails = await this.fetchUserDetails({ force: true });
      return userDetails?.Role || null;
    },

    async fetchUserDetails({ force = false } = {}) {
      if (!force && this.userDetails && Object.keys(this.userDetails).length) {
        return this.userDetails;
      }

      if (!this.accessToken) {
        const hasSession = await this.ensureValidSession();
        if (!hasSession) {
          throw new Error('No active session');
        }
      }

      try {
        const response = await axios.get(apiUrl('/nora_user/details'), {
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
          },
        });

        const userInfo = response.data.user_info || {};
        this.setUserDetails(userInfo);
        return userInfo;
      } catch (error) {
        console.error('Error fetching user details:', error);
        throw error;
      }
    },

    async refreshAccessToken() {
      if (!this.refreshToken) {
        throw new Error('No refresh token available');
      }

      if (refreshPromise) {
        return refreshPromise;
      }

      refreshPromise = axios.post(
        apiUrl(`/nora_user/token/refresh?refresh_token=${this.refreshToken}`)
      ).then((response) => {
        const { access_token, refresh_token, token_type } = response.data;
        this.setSession({
          accessToken: access_token,
          refreshToken: refresh_token,
          tokenType: token_type,
        });

        return access_token;
      }).catch((error) => {
        console.error('Error refreshing token:', error);
        this.logout();
        throw error;
      }).finally(() => {
        refreshPromise = null;
      });

      return refreshPromise;
    },

    logout({ redirectToLogin = false } = {}) {
      this.clearRefreshTimer();
      this.accessToken = null;
      this.refreshToken = null;
      this.tokenType = null;
      this.userRole = null;
      this.userDetails = {};
      this.sessionInitialized = false;
      this.requestState.error = null;
      this.persistSession();

      if (redirectToLogin && window.location.pathname !== '/login') {
        window.location.assign('/login');
      }
    },

    handleError(error) {
      let errorMessage = 'An unexpected error occurred. Please try again later.';

      if (error.message === 'Unauthorized role') {
        errorMessage = 'Access denied. You do not have the required permissions to access this app.';
      } else if (error.response) {
        switch (error.response.status) {
          case 400:
            errorMessage = 'Invalid username or password.';
            break;
          case 401:
            errorMessage = 'Unauthorized access.';
            break;
          case 500:
            errorMessage = 'Server error.';
            break;
          default:
            errorMessage = 'Something went wrong.';
        }
      } else if (error.request) {
        errorMessage = 'No response from server.';
      }

      return errorMessage;
    },

    async get_url() {
      return getRoleApiPath(this.userRole);
    },
  },
});
